// The company's model policy as the picker and Settings read it (COORD
// model-picker contract, accepted by ORCH3 2026-10-05). The server sends keys
// only; names come from the generated registry and copy from ns/model-picker.
// When the policy route is absent (an older server), the picker stays hidden
// and Chat keeps today's behaviour.
import { companyRequest, CompanyRequestError } from '@/company/api';
import { MODEL_CHOICE_COPY } from '../model-choices.generated';

export const EFFORTS = ['fast', 'balanced', 'thorough', 'max'] as const;
export type Effort = (typeof EFFORTS)[number];
export type CostHint = 'lower' | 'standard' | 'higher' | null;

export interface AvailableChoice {
  choice: string;
  efforts: Effort[];
  cost: CostHint;
  /** The registry name, when the server sends one. */
  name?: string;
}

export interface ModelPolicy {
  organizationId: string;
  revision: number;
  allowedChoices: string[];
  defaultChoice: string;
  allowedEfforts: Effort[];
  defaultEffort: Effort;
  /** The limit per run, in whole credits. Never a currency amount. */
  runCapCredits: number;
}

export interface ModelPolicyState {
  policy: ModelPolicy;
  available: {
    choices: AvailableChoice[];
    runCapCredits: { min: number; max: number; default: number };
  };
}

export interface PickerModel {
  choice: string;
  name: string;
  /** Supported by the model and allowed by the company, in EFFORTS order. */
  efforts: Effort[];
  cost: CostHint;
}

export interface Selection {
  choice: string;
  effort: Effort;
}

export const isEffort = (value: unknown): value is Effort => EFFORTS.includes(value as Effort);

const policyPath = (organizationId: string) =>
  `/api/v1/company/model-policy?organization_id=${encodeURIComponent(organizationId)}`;

const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/** The shape the picker relies on; anything else counts as no policy. */
export function isModelPolicyState(value: unknown): value is ModelPolicyState {
  const state = value as ModelPolicyState | null;
  const policy = state?.policy;
  const cap = state?.available?.runCapCredits;
  return Boolean(
    policy &&
    typeof policy.organizationId === 'string' &&
    Number.isFinite(policy.revision) &&
    strings(policy.allowedChoices) &&
    typeof policy.defaultChoice === 'string' &&
    strings(policy.allowedEfforts) &&
    isEffort(policy.defaultEffort) &&
    Number.isFinite(policy.runCapCredits) &&
    Array.isArray(state.available?.choices) &&
    state.available.choices.every(
      (item) => typeof item?.choice === 'string' && strings(item.efforts),
    ) &&
    cap &&
    Number.isFinite(cap.min) &&
    Number.isFinite(cap.max),
  );
}

/** Null when this server has no model policy yet: the picker then stays hidden. */
export async function fetchModelPolicy(organizationId: string): Promise<ModelPolicyState | null> {
  try {
    const state = await companyRequest<unknown>(policyPath(organizationId));
    return isModelPolicyState(state) ? state : null;
  } catch (cause) {
    if (cause instanceof CompanyRequestError && [404, 405, 501].includes(cause.status)) return null;
    throw cause;
  }
}

export const saveModelPolicy = (
  policy: Omit<ModelPolicy, 'organizationId'> & { organizationId: string },
) => companyRequest<ModelPolicyState>('/api/v1/company/model-policy', 'PATCH', policy);

export function modelName(choice: string, serverName?: string): string {
  return (
    serverName ??
    (MODEL_CHOICE_COPY as Record<string, { name: string } | undefined>)[choice]?.name ??
    choice.charAt(0).toUpperCase() + choice.slice(1)
  );
}

/** What a person may pick: the company's allowed models, each with its allowed efforts. */
export function pickerModels(state: ModelPolicyState): PickerModel[] {
  const allowed = new Set(state.policy.allowedChoices);
  const efforts = new Set(state.policy.allowedEfforts);
  return state.available.choices
    .filter((item) => allowed.has(item.choice))
    .map((item) => ({
      choice: item.choice,
      name: modelName(item.choice, item.name),
      efforts: EFFORTS.filter((effort) => item.efforts.includes(effort) && efforts.has(effort)),
      cost: item.cost,
    }))
    .filter((item) => item.efforts.length > 0);
}

/** The wanted effort if this model has it, else the nearest lower one, else its lowest. */
export function effortFor(model: PickerModel | undefined, wanted: Effort): Effort {
  if (!model?.efforts.length) return wanted;
  if (model.efforts.includes(wanted)) return wanted;
  const at = EFFORTS.indexOf(wanted);
  const lower = [...model.efforts].reverse().find((effort) => EFFORTS.indexOf(effort) < at);
  return lower ?? model.efforts[0];
}

/** A chat's saved choice when it is still allowed, otherwise the company default. */
export function resolveSelection(
  state: ModelPolicyState,
  saved: { choice?: string | null; effort?: string | null } | null,
): Selection {
  const models = pickerModels(state);
  const model =
    models.find((item) => item.choice === saved?.choice) ??
    models.find((item) => item.choice === state.policy.defaultChoice) ??
    models[0];
  const wanted = isEffort(saved?.effort) ? saved.effort : state.policy.defaultEffort;
  return {
    choice: model?.choice ?? state.policy.defaultChoice,
    effort: effortFor(model, wanted),
  };
}

// The thread's saved choice. 404: the chat has no row yet (a new chat).
export async function fetchThreadSelection(
  threadId: string,
): Promise<{ choice: string; effort: string | null } | null> {
  try {
    const saved = await companyRequest<{ modelChoice: string; effort?: string | null }>(
      `/api/v1/threads/${encodeURIComponent(threadId)}/model-choice`,
    );
    return { choice: saved.modelChoice, effort: saved.effort ?? null };
  } catch (cause) {
    if (cause instanceof CompanyRequestError && cause.status === 404) return null;
    throw cause;
  }
}

export async function saveThreadSelection(threadId: string, selection: Selection) {
  try {
    await companyRequest(`/api/v1/threads/${encodeURIComponent(threadId)}/model-choice`, 'PATCH', {
      modelChoice: selection.choice,
      effort: selection.effort,
    });
  } catch {
    // A new chat has no row yet, and a failed save is not lost: the choice
    // travels with the next message (captureModelSelection) and is saved there.
  }
}
